import {
  cn,
  Combobox,
  type ComboboxOption,
  type ComboboxOptionRenderProps,
  DeletableBadge,
  RadioGroupButtonItem,
  RadioGroupButtons,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StringSelect,
} from "@tellescope/ui";
import { useAuth, useTellescopeQuery, useUsersQuery } from "@tellescope/tanstack-query";
import { formatName } from "@tellescope/utils";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type VitalsFilters, useVitalProgramsAndThresholds } from "../../hooks";
import { type Enduser, EnduserSearchCombobox } from "../EnduserSearchCombobox";
import { Activity, Check, User, UsersRound } from "lucide-react";
import { i18n } from "@tellescope/i18n";
import { CLASSIFICATION_CLASS_NAMES } from "./cells";

const GROUP_VALUE_PREFIX = "group::";

type CareTeamOptionMetadata = boolean;

const formatCareTeamOption = ({ option, isSelected }: ComboboxOptionRenderProps<CareTeamOptionMetadata>) => {
  const isGroup = option.metadata?.isGroup;
  return (
    <>
      <div className="flex items-center gap-2 w-full">
        {isGroup && <UsersRound size={14} className="shrink-0 text-primary" />}
        <div className="flex flex-col gap-1 w-full min-w-0">
          <div className={cn("font-medium text-sm truncate", isGroup && "text-primary")}>{option.label}</div>
          {option.description && <div className="text-xs text-muted-foreground truncate">{option.description}</div>}
        </div>
      </div>
      <Check className={cn("ml-auto h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
    </>
  );
};

const getClassificationOptions = () => [
  { value: "Target", label: i18n.t("Target", { ns: "vitals" }) },
  { value: "Low", label: i18n.t("Low", { ns: "vitals" }) },
  { value: "High", label: i18n.t("High", { ns: "vitals" }) },
  { value: "Very Low", label: i18n.t("Very Low", { ns: "vitals" }) },
  { value: "Very High", label: i18n.t("Very High", { ns: "vitals" }) },
  { value: "Critical Low", label: i18n.t("Critical Low", { ns: "vitals" }) },
  { value: "Critical High", label: i18n.t("Critical High", { ns: "vitals" }) },
];

export interface VitalsFilterConfigFormProps {
  stagedFilters: VitalsFilters;
  onStagedFiltersChange: (filters: VitalsFilters) => void;
  sourceOptions?: string[];
}

export const VitalsFilterConfigForm = ({
  stagedFilters,
  onStagedFiltersChange,
  sourceOptions = [],
}: VitalsFilterConfigFormProps) => {
  const { t } = useTranslation("vitals");

  const { selectedOrganization } = useAuth();
  const { data: users = [] } = useUsersQuery();

  const userComboboxOptions = useMemo<ComboboxOption<CareTeamOptionMetadata>[]>(
    () =>
      users.map((user) => ({
        value: user.id,
        label: formatName(user) || user.id,
      })),
    [users]
  );

  const groupComboboxOptions = useMemo<ComboboxOption<CareTeamOptionMetadata>[]>(
    () =>
      (selectedOrganization?.groups ?? []).map((group) => {
        const memberCount = users.filter((u) => u.tags?.includes(group)).length;
        return {
          value: `${GROUP_VALUE_PREFIX}${group}`,
          label: group,
          description: t("{{count}} members", { count: memberCount }),
          metadata: { isGroup: true },
        };
      }),
    [selectedOrganization?.groups, users, t]
  );

  const careTeamComboboxOptions = useMemo<ComboboxOption<CareTeamOptionMetadata>[]>(
    () => [...groupComboboxOptions, ...userComboboxOptions],
    [groupComboboxOptions, userComboboxOptions]
  );

  const selectedCareTeamOptions = useMemo<ComboboxOption<CareTeamOptionMetadata>[]>(
    () =>
      (stagedFilters.careTeamIds ?? []).map(
        (id) => userComboboxOptions.find((u) => u.value === id) ?? { value: id, label: id }
      ),
    [stagedFilters.careTeamIds, userComboboxOptions]
  );

  const sourceComboboxOptions = useMemo(
    () => sourceOptions.map((source) => ({ value: source, label: source })),
    [sourceOptions]
  );

  const selectedSourceOptions = useMemo(
    () =>
      (stagedFilters.source ?? []).map(
        (source) => sourceComboboxOptions.find((s) => s.value === source) ?? { value: source, label: source }
      ),
    [stagedFilters.source, sourceComboboxOptions]
  );

  const setFilterField = useCallback(
    <K extends keyof VitalsFilters>(key: K, value: VitalsFilters[K]) => {
      onStagedFiltersChange({ ...stagedFilters, [key]: value });
    },
    [onStagedFiltersChange, stagedFilters]
  );

  const handleCareTeamChange = useCallback(
    (selected: ComboboxOption<CareTeamOptionMetadata>[]) => {
      const currentIds = new Set(stagedFilters.careTeamIds ?? []);

      for (const option of selected) {
        if (option.value.startsWith(GROUP_VALUE_PREFIX)) {
          // Group selected — expand to member user IDs
          const groupName = option.value.slice(GROUP_VALUE_PREFIX.length);
          for (const user of users) {
            if (user.tags?.includes(groupName)) {
              currentIds.add(user.id);
            }
          }
        } else {
          currentIds.add(option.value);
        }
      }

      // Find IDs that were deselected (were in previous selection but not in new non-group selections)
      const newNonGroupIds = new Set(
        selected.filter((s) => !s.value.startsWith(GROUP_VALUE_PREFIX)).map((s) => s.value)
      );
      const previousIds = stagedFilters.careTeamIds ?? [];
      for (const id of previousIds) {
        if (!newNonGroupIds.has(id)) {
          currentIds.delete(id);
        }
      }

      const ids = Array.from(currentIds);
      setFilterField("careTeamIds", ids.length > 0 ? ids : undefined);
    },
    [stagedFilters.careTeamIds, users, setFilterField]
  );

  const { data: queriedEndusers } = useTellescopeQuery({
    model: "endusers",
    method: "getByIds",
    request: {
      ids: stagedFilters.enduserIds ?? [],
    },
    enabled: !!stagedFilters.enduserIds?.length,
    select: (data) => data.matches,
  });

  const handleEnduserChange = useCallback(
    (endusers: Enduser[]) => {
      setFilterField("enduserIds", endusers.length > 0 ? endusers.map((e) => e.id) : undefined);
    },
    [setFilterField]
  );

  const { vitalPrograms, programOptions, thresholdOptions, defaultProgramConfigIds } = useVitalProgramsAndThresholds();

  const selectedThresholdOptions = useMemo(
    () =>
      stagedFilters.programId
        ? (stagedFilters.threshholds ?? []).map(
            (id) => thresholdOptions.find((t) => t.value === id) ?? { value: id as string, label: id as string }
          )
        : (defaultProgramConfigIds ?? []).map(
            (id) => thresholdOptions.find((t) => t.value === id) ?? { value: id, label: id }
          ),
    [stagedFilters.programId, stagedFilters.threshholds, thresholdOptions, defaultProgramConfigIds]
  );

  const handleProgramSelect = useCallback(
    (programTitle: string) => {
      const configurationIds = vitalPrograms?.programs.find(({ title }) => title === programTitle)?.configurationIds;
      onStagedFiltersChange({
        ...stagedFilters,
        programId: programTitle,
        threshholds: configurationIds?.length ? configurationIds : undefined,
      });
    },
    [onStagedFiltersChange, stagedFilters, vitalPrograms?.programs]
  );

  return (
    <div className="space-y-4 font-semibold">
      <div className="space-y-4 font-normal">
        <div className="space-y-1.5">
          <span
            className={cn(
              "text-sm font-medium flex gap-2 items-center",
              stagedFilters.enduserIds?.length && "text-green-600"
            )}
          >
            <User size={16} />
            {t("Patients")}
          </span>
          <EnduserSearchCombobox
            value={queriedEndusers ?? []}
            onChange={handleEnduserChange}
            multiple
            placeholder={t("Select patients")}
            onlyDisplayCount
            showAsBadges
            clearable={false}
          />
        </div>
        <div className="space-y-1.5">
          <span
            className={cn(
              "text-sm font-medium flex gap-2 items-center",
              selectedCareTeamOptions.length && "text-green-600"
            )}
          >
            <Activity size={16} />
            {t("Care Team")}
          </span>
          <Combobox
            value={selectedCareTeamOptions}
            onChange={(selected) => {
              if (Array.isArray(selected)) {
                handleCareTeamChange(selected);
              }
            }}
            options={careTeamComboboxOptions}
            multiple
            placeholder={t("Select care team members")}
            searchPlaceholder={t("Search users or groups...")}
            formatSelectedValue={({ length }) => t("{{amount}} selected", { amount: length })}
            formatOption={formatCareTeamOption}
          />
          <div className="flex flex-wrap gap-1">
            {selectedCareTeamOptions.map((user) => (
              <DeletableBadge
                key={user.value}
                onDelete={() =>
                  setFilterField(
                    "careTeamIds",
                    selectedCareTeamOptions.reduce<string[]>((acc, { value }) => {
                      if (value === user.value) return acc;
                      acc.push(value);
                      return acc;
                    }, [])
                  )
                }
              >
                {user.label}
              </DeletableBadge>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <span className={cn("text-sm font-medium", stagedFilters.programId && "text-green-600")}>
              {t("Program")}
            </span>
            <StringSelect
              value={stagedFilters.programId ?? programOptions.at(0)?.value}
              onValueChange={handleProgramSelect}
              options={programOptions}
              placeholder={t("Select Program")}
              className="w-full"
            />
          </div>
          <div className="space-y-1.5">
            <span className={cn("text-sm font-medium", !!stagedFilters.threshholds?.length && "text-green-600")}>
              {t("Vitals Thresholds")}
            </span>
            <Combobox
              value={selectedThresholdOptions}
              onChange={(selected) => {
                const ids = Array.isArray(selected) ? selected.map((s) => s.value) : [];
                setFilterField("threshholds", ids.length > 0 ? ids : undefined);
              }}
              options={thresholdOptions}
              multiple
              placeholder={t("Select thresholds")}
              searchPlaceholder={t("Search thresholds...")}
              formatSelectedValue={({ length }) => t("{{amount}} selected", { amount: length })}
            />
            <div className="flex flex-wrap gap-1">
              {selectedThresholdOptions.map((threshold) => (
                <DeletableBadge
                  key={threshold.value}
                  onDelete={() =>
                    setFilterField(
                      "threshholds",
                      selectedThresholdOptions.reduce<string[]>((acc, { value }) => {
                        if (value === threshold.value) return acc;
                        acc.push(value);
                        return acc;
                      }, [])
                    )
                  }
                >
                  {threshold.label}
                </DeletableBadge>
              ))}
            </div>
          </div>
        </div>
        <div className="space-y-1.5">
          <span className={cn("text-sm font-medium")}>{t("Classification")}</span>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <span className="text-xs text-muted-foreground">{t("Match Type")}</span>
              <Select
                value={stagedFilters.classification?.type ?? "any"}
                onValueChange={(val) => {
                  if (val === "all" || val === "any") {
                    setFilterField("classification", {
                      type: val,
                      triggers: stagedFilters.classification?.triggers ?? [],
                    });
                  }
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("Select type")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("All of")}</SelectItem>
                  <SelectItem value="any">{t("Any of")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <span className="text-xs text-muted-foreground">{t("Select Triggers")}</span>
              <Combobox<string, true>
                value={
                  stagedFilters.classification?.triggers.reduce<ComboboxOption<string>[]>((acc, curr) => {
                    const match = getClassificationOptions().find(({ value }) => value === curr);
                    if (!match) return acc;

                    acc.push(match);
                    return acc;
                  }, []) ?? []
                }
                onChange={(selected) => {
                  const triggers = Array.isArray(selected) ? selected.map(({ value }) => value) : [];
                  setFilterField("classification", {
                    type: stagedFilters.classification?.type ?? "any",
                    triggers,
                  });
                }}
                options={getClassificationOptions()}
                multiple
                placeholder={t("Select triggers")}
                searchPlaceholder={t("Search triggers...")}
                formatSelectedValue={({ length }) => t("{{amount}} selected", { amount: length })}
              />
            </div>
          </div>
          <div className="col-span-2 flex flex-wrap gap-2">
            {stagedFilters.classification?.triggers.map((trigger) => (
              <DeletableBadge
                key={trigger}
                onDelete={() =>
                  setFilterField("classification", {
                    type: stagedFilters.classification?.type ?? "any",
                    triggers: stagedFilters.classification?.triggers.filter((item) => item !== trigger) ?? [],
                  })
                }
                className={cn(CLASSIFICATION_CLASS_NAMES[trigger], "border-none")}
              >
                {trigger}
              </DeletableBadge>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <span className={cn("text-sm font-medium", !!stagedFilters.source?.length && "text-green-600")}>
            {t("Source")}
          </span>
          <Combobox
            value={selectedSourceOptions}
            onChange={(selected) => {
              const sources = Array.isArray(selected) ? selected.map((s) => s.value) : [];
              setFilterField("source", sources.length > 0 ? sources : undefined);
            }}
            options={sourceComboboxOptions}
            multiple
            placeholder={t("Select sources")}
            searchPlaceholder={t("Search sources...")}
            formatSelectedValue={({ length }) => t("{{amount}} selected", { amount: length })}
          />
          <div className="flex flex-wrap gap-1">
            {selectedSourceOptions.map((source) => (
              <DeletableBadge
                key={source.value}
                onDelete={() =>
                  setFilterField(
                    "source",
                    selectedSourceOptions.reduce<string[]>((acc, { value }) => {
                      if (value === source.value) return acc;
                      acc.push(value);
                      return acc;
                    }, [])
                  )
                }
              >
                {source.label}
              </DeletableBadge>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Validity")}</span>
          <RadioGroupButtons
            value={stagedFilters.validStatus ?? "valid"}
            onValueChange={(val) => setFilterField("validStatus", val as VitalsFilters["validStatus"])}
            selectedVariant="default"
            unselectedVariant="outline"
          >
            <RadioGroupButtonItem value="valid" className="rounded-full" data-testid="vitals-filter-validity-valid">
              {t("Valid")}
            </RadioGroupButtonItem>
            <RadioGroupButtonItem value="invalid" className="rounded-full" data-testid="vitals-filter-validity-invalid">
              {t("Invalid")}
            </RadioGroupButtonItem>
            <RadioGroupButtonItem value="all" className="rounded-full" data-testid="vitals-filter-validity-all">
              {t("All")}
            </RadioGroupButtonItem>
          </RadioGroupButtons>
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">{t("Reviewed Status")}</span>
          <RadioGroupButtons
            value={stagedFilters.reviewed ?? "all"}
            onValueChange={(val) => setFilterField("reviewed", val as VitalsFilters["reviewed"])}
            selectedVariant="default"
            unselectedVariant="outline"
          >
            <RadioGroupButtonItem value="all" className="rounded-full" data-testid="vitals-filter-reviewed-all">
              {t("All")}
            </RadioGroupButtonItem>
            <RadioGroupButtonItem
              value="reviewed"
              className="rounded-full"
              data-testid="vitals-filter-reviewed-reviewed"
            >
              {t("Reviewed")}
            </RadioGroupButtonItem>
            <RadioGroupButtonItem
              value="unreviewed"
              className="rounded-full"
              data-testid="vitals-filter-reviewed-unreviewed"
            >
              {t("Not Reviewed")}
            </RadioGroupButtonItem>
          </RadioGroupButtons>
        </div>
      </div>
    </div>
  );
};
